import { Component, OnInit, OnDestroy, Inject, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormBuilder, FormGroup, FormArray, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { debounceTime, distinctUntilChanged, finalize, map, Observable, of, startWith, Subject, switchMap, takeUntil } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { ToastrService } from 'ngx-toastr';
import { NgxPrintModule } from 'ngx-print';
import { MatTableDataSource } from '@angular/material/table';
import { MaterialModule } from 'src/app/material.module';
import { IndianCurrencyPipe } from 'src/app/pipe/indian-currency.pipe';
import { DefaultValuePipe } from 'src/app/pipe/default-value.pipe';
import { CommonDropDown } from 'src/app/common/types/dropdown.type';
import { LookupService } from 'src/app/services/lookup.service';
import { HospitalStateService } from 'src/app/services/hospitalstate.service';
import { DiscountType, DiscountTypeLabels } from 'src/app/common/enums/discount-type';
import { PharmacyReceiptComponent } from '../pharmacy-receipt/pharmacy-receipt.component';
import { PaymentComponent } from 'src/app/pages/dialogs/payment/payment.component';
import { PaymentService } from 'src/app/pages/dialogs/payment/payment.service';
import { IPaymentList } from 'src/app/pages/dialogs/payment/ipayment';
import {
  DrugInventoryLookup,
  DrugBatchItem,
  PharmacyBillCommand,
  PharmacyBillResult,
  DispenseDialogData,
} from 'src/app/models/pharmacy.model';
import { PharmacyService } from '../pharmacy.service';
import { IUsers } from '../../user/iusers';
import { IHospitalInfo } from '../../hospital/ihospital-info';
import { UserService } from '../../user/user.service';
import { DialogUtils } from 'src/app/common/utils/dialog.utils';

@Component({
  selector: 'app-dispense-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MaterialModule,
    TablerIconsModule,
    TranslateModule,
    ReactiveFormsModule,
    IndianCurrencyPipe,
    DefaultValuePipe,
    NgxPrintModule,
    PharmacyReceiptComponent,
  ],
  providers: [IndianCurrencyPipe],
  templateUrl: './dispense-dialog.component.html',
  styleUrl: './dispense-dialog.component.scss',
})
export class DispenseDialogComponent implements OnInit, OnDestroy {

  private destroy$ = new Subject<void>();

  isViewMode: boolean;
  isLoading = false;
  billSaved = false;

  // View mode
  billResult: PharmacyBillResult | null = null;

  // Create mode
  dispenseForm: FormGroup;
  patientCtrl = new FormControl(null, [Validators.required]);
  doctorCtrl = new FormControl();
  filteredPatients$: Observable<CommonDropDown[]>;
  filteredDoctors$: Observable<CommonDropDown[]>;
  inventoryLookup: DrugInventoryLookup[] = [];
  subTotal = 0;
  totalGST = 0;
  sgst = 0;
  cgst = 0;
  discount = 0;
  roundOff = 0;
  grandTotal = 0;
  selectedPatientId: number | null = null;
  selectedDoctorId: number | null = null;
  printMode = false;
  hospitalInfo: IHospitalInfo;
  doctorInfo: IUsers;
  DiscountType = DiscountType;
  DiscountTypeLabels = DiscountTypeLabels;

  // Tab management
  activeTabIndex = 0;

  // Payment properties
  paidAmount = 0;
  dueAmount = 0;
  paymentCount = 0;
  payments: IPaymentList[] = [];
  paymentDataSource = new MatTableDataSource<IPaymentList>();
  paymentDisplayedColumns: string[] = ['paymentDate', 'paymentMethod', 'transactionNo', 'description', 'amount'];

  @ViewChild('printBtn') printBtn: ElementRef;
  @ViewChild(PharmacyReceiptComponent) receiptComponent: PharmacyReceiptComponent;

  constructor(
    private fb: FormBuilder,
    private pharmacyService: PharmacyService,
    private lookupService: LookupService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
    private hospitalStateService: HospitalStateService,
    private dialog: MatDialog,
    private paymentService: PaymentService,
    private dialogRef: MatDialogRef<DispenseDialogComponent>,
    private _userService: UserService,  
    @Inject(MAT_DIALOG_DATA) public data: DispenseDialogData,
    private dialogUtils: DialogUtils,
  ) { }

  ngOnInit(): void {
    this.isViewMode = this.data.mode === 'view';
    this.hospitalInfo = this.hospitalStateService.getHospital();

    if (this.isViewMode) {
      this.loadBillById(this.data.billId!);
    } else {
      this.initCreateMode();
    }
  }

  // ==========================================
  // VIEW MODE
  // ==========================================

  private loadBillById(billId: number): void {
    this.isLoading = true;
    this.pharmacyService.getPharmacyBillById(billId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.isLoading = false;
          if (response.isSuccess) {
            this.billResult = response.result;
            this.updatePaymentSummaryFromBill();
            this.loadPaymentHistory();
            if (this.billResult.doctorId) {
              this.fetchDoctorInfo(this.billResult.doctorId);
            }
          }
        },
        error: () => this.isLoading = false,
      });
  }

  private updatePaymentSummaryFromBill(): void {
    if (!this.billResult) return;
    this.paidAmount = this.billResult.paidAmount || 0;
    this.dueAmount = Math.max(0, this.billResult.amount - this.paidAmount);
  }

  onPrintBill(): void {
    if (!this.billResult) return;
    this.printMode = true;
    this.cdr.detectChanges();

    if (this.receiptComponent) {
      this.receiptComponent.createPrintContent().then(content => {
        this.openPrintWindow(content);
      }).catch(err => {
        console.error('Error in onPrintBill:', err);
        this.toastr.error('Failed to generate print content');
      });
    }
  }

  private openPrintWindow(content: string): void {
    const printFrame = document.createElement('iframe');
    printFrame.id = 'print-iframe';
    printFrame.style.display = 'none';
    document.body.appendChild(printFrame);

    const doc = printFrame.contentWindow?.document || printFrame.contentDocument;
    if (doc) {
      doc.open();
      doc.write(content);
      doc.close();

      setTimeout(() => {
        if (printFrame.contentWindow) {
          printFrame.contentWindow.focus();
          printFrame.contentWindow.print();
          printFrame.contentWindow.onafterprint = () => {
            if (printFrame.parentNode) {
              printFrame.parentNode.removeChild(printFrame);
            }
          };
        } else {
          if (printFrame.parentNode) {
            printFrame.parentNode.removeChild(printFrame);
          }
        }
      }, 500);
    } else {
      if (printFrame.parentNode) {
        printFrame.parentNode.removeChild(printFrame);
      }
    }
  }

  fetchDoctorInfo(doctorId?: number) {
    if (!doctorId) return;
    this._userService
      .getUserByID(doctorId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => this.doctorInfo = res.result);
  }

  // ==========================================
  // PAYMENT
  // ==========================================

  onTabChange(index: number): void {
    this.activeTabIndex = index;
    if (index === 1) {
      this.loadPaymentHistory();
    }
  }

  loadPaymentHistory(): void {
    if (!this.billResult?.id) return;

    this.paymentService.getPatientPaymentHistory(this.billResult.id).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        if (response.isSuccess && response.result) {
          const payments = Array.isArray(response.result) ? response.result : [response.result];
          this.payments = payments.map(payment => ({
            ...payment,
            transactionNo: (payment as any).transactionNumber,
            description: (payment as any).description,
          }));
          this.paymentDataSource.data = this.payments;
          this.paymentCount = this.payments.length;
          this.calculatePaymentSummary();
        } else {
          this.paymentCount = 0;
        }
      },
      error: () => {
        this.payments = [];
        this.paymentCount = 0;
        this.calculatePaymentSummary();
      }
    });
  }

  calculatePaymentSummary(): void {
    this.paidAmount = this.payments.reduce((sum, payment) => sum + (payment.amount || 0), 0);
    this.dueAmount = Math.max(0, (this.billResult?.amount || 0) - this.paidAmount);
  }

  takePayment(): void {
    if (!this.billResult?.id || this.dueAmount <= 0) return;

    this.dialog.open(PaymentComponent, {
      autoFocus: false,
      height: '60vh',
      maxWidth: '80vw',
      data: { billId: this.billResult.id }
    }).afterClosed().subscribe((result) => {
      if (result) {
        this.reloadBillAfterPayment();
      }
    });
  }

  private reloadBillAfterPayment(): void {
    if (!this.billResult?.id) return;
    this.billSaved = true;
    this.loadBillById(this.billResult.id);
  }

  // ==========================================
  // CREATE MODE
  // ==========================================

  private initCreateMode(): void {
    this.dispenseForm = this.fb.group({
      discountType: [DiscountType.FixedAmount],
      discount: [0],
      roundOff: [0],
      items: this.fb.array([this.createRow()]),
    });
    this.setupDiscountListener();
    this.loadInventoryLookup();
    this.setupPatientAutocomplete();
    this.setupDoctorAutocomplete();
  }

  private setupDiscountListener(): void {
    this.dispenseForm.get('discount')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.recalculateTotals());

    this.dispenseForm.get('discountType')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.recalculateTotals());

    this.dispenseForm.get('roundOff')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.recalculateTotals());
  }

  get items(): FormArray {
    return this.dispenseForm.get('items') as FormArray;
  }

  createRow(): FormGroup {
    return this.fb.group({
      drugInventoryId: [null],
      drugBatchId: [null],
      drugName: [''],
      batchNo: [''],
      expiryDate: [null],
      unitPrice: [0],
      gstPercentage: [0],
      quantity: [1, [Validators.required, Validators.min(1)]],
      totalPrice: [0],
      drugCtrl: [''],
      batches: [[] as DrugBatchItem[]],
      selectedBatch: [null],
    });
  }

  onAddRow(): void {
    this.items.push(this.createRow());
    this.setupDrugFilterForRow(this.items.length - 1);
  }

  onRemoveRow(index: number): void {
    if (this.items.length <= 1) {
      this.items.at(0).reset();
    } else {
      this.items.removeAt(index);
    }
    this.recalculateTotals();
  }

  getFilteredDrugsForRow(index: number): Observable<DrugInventoryLookup[]> {
    const row = this.items.at(index);
    const ctrl = row.get('drugCtrl');
    if (!ctrl) return of([]);

    return ctrl.valueChanges.pipe(
      startWith(ctrl.value || ''),
      map(value => {
        const filterVal = typeof value === 'string' ? value.toLowerCase() : '';
        return this.inventoryLookup.filter(d =>
          d.drugName.toLowerCase().includes(filterVal) && d.totalStock > 0
        );
      })
    );
  }

  setupDrugFilterForRow(_index: number): void {
    // Filter is computed on-the-fly via getFilteredDrugsForRow
  }

  displayDrugLookup(drug: DrugInventoryLookup): string {
    return drug ? drug.drugName : '';
  }

  onDrugSelected(drug: DrugInventoryLookup, row: AbstractControl): void {
    const formRow = row as FormGroup;
    formRow.patchValue({
      drugInventoryId: drug.id,
      drugName: drug.drugName,
      unitPrice: drug.sellingPrice,
      gstPercentage: drug.gstPercentage,
    });
    this.loadBatchesForRow(drug.id, formRow);
  }

  private loadBatchesForRow(drugInventoryId: number, row: FormGroup): void {
    this.pharmacyService.getBatches(drugInventoryId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        if (response.isSuccess && response.result.length > 0) {
          row.patchValue({ batches: response.result });
          this.autoSelectEarliestBatch(response.result, row);
        }
      });
  }

  private autoSelectEarliestBatch(batches: DrugBatchItem[], row: FormGroup): void {
    const selectedBatchIds = this.items.controls
      .map(c => c.get('drugBatchId')?.value)
      .filter(id => id !== null);

    const sorted = [...batches]
      .filter(b => b.quantity > 0 && !selectedBatchIds.includes(b.id))
      .sort((a, b) => new Date(a.expiryDate).getTime() - new Date(b.expiryDate).getTime());

    if (sorted.length > 0) {
      this.selectBatch(sorted[0], row);
    } else if (batches.length > 0) {
      // Fallback: If all batches with stock are already selected,
      // pick the earliest one anyway (user will get validation error or should pick another drug)
      // or just select nothing. Picking the earliest that is available is better than nothing.
      const fallback = [...batches]
        .filter(b => b.quantity > 0)
        .sort((a, b) => new Date(a.expiryDate).getTime() - new Date(b.expiryDate).getTime());
      if (fallback.length > 0) {
        this.selectBatch(fallback[0], row);
      }
    }
  }

  getAvailableBatches(row: AbstractControl): DrugBatchItem[] {
    const formRow = row as FormGroup;
    const allBatches = (formRow.get('batches')?.value as DrugBatchItem[]) || [];
    const currentBatchId = formRow.get('drugBatchId')?.value;

    const otherSelectedBatchIds = this.items.controls
      .filter(c => c !== formRow)
      .map(c => c.get('drugBatchId')?.value)
      .filter(id => id !== null);

    return allBatches.filter(b => b.id === currentBatchId || !otherSelectedBatchIds.includes(b.id));
  }

  onBatchSelected(batch: DrugBatchItem, row: AbstractControl): void {
    this.selectBatch(batch, row as FormGroup);
  }

  private selectBatch(batch: DrugBatchItem, row: FormGroup): void {
    row.patchValue({
      drugBatchId: batch.id,
      batchNo: batch.batchNo,
      expiryDate: batch.expiryDate,
      selectedBatch: batch,
    });
    this.recalculateRowTotal(row);
  }

  onQuantityChanged(row: AbstractControl): void {
    const formRow = row as FormGroup;
    const selectedBatch = formRow.get('selectedBatch')?.value as DrugBatchItem;
    const enteredQty = Number(formRow.get('quantity')?.value) || 0;

    if (selectedBatch && enteredQty > selectedBatch.quantity) {
      // this.toastr.warning(
      //   `Only ${selectedBatch.quantity} available in batch ${selectedBatch.batchNo}`,
      //   'Insufficient Stock'
      // );
      formRow.patchValue({ quantity: selectedBatch.quantity });
    }

    this.recalculateRowTotal(formRow);
  }

  private recalculateRowTotal(row: FormGroup): void {
    const qty = Number(row.get('quantity')?.value) || 0;
    const unitPrice = Number(row.get('unitPrice')?.value) || 0;
    const gstPct = Number(row.get('gstPercentage')?.value) || 0;
    const baseAmount = qty * unitPrice;
    const gstAmount = baseAmount * gstPct / 100;
    row.patchValue({ totalPrice: baseAmount + gstAmount }, { emitEvent: false });
    this.recalculateTotals();
  }

  recalculateTotals(): void {
    let sub = 0;
    let gst = 0;

    this.items.controls.forEach(row => {
      const qty = Number(row.get('quantity')?.value) || 0;
      const unitPrice = Number(row.get('unitPrice')?.value) || 0;
      const gstPct = Number(row.get('gstPercentage')?.value) || 0;
      const baseAmount = qty * unitPrice;
      sub += baseAmount;
      gst += baseAmount * gstPct / 100;
    });

    this.subTotal = sub;
    this.totalGST = gst;
    this.sgst = gst / 2;
    this.cgst = gst / 2;

    const discountAmount = this.calculateDiscountAmount(sub + gst);
    const roundOffVal = Number(this.dispenseForm.get('roundOff')?.value) || 0;
    this.discount = discountAmount;
    this.roundOff = roundOffVal;
    this.grandTotal = sub + gst - discountAmount + roundOffVal;

    this.cdr.detectChanges();
  }

  private calculateDiscountAmount(total: number): number {
    const discountType = this.dispenseForm.get('discountType')?.value;
    const discountValue = Number(this.dispenseForm.get('discount')?.value) || 0;
    if (discountType === DiscountType.Percentage) {
      return total * discountValue / 100;
    }
    return discountValue;
  }

  onSave(): void {
    if (!this.selectedPatientId) {
      this.patientCtrl.markAsTouched();
      this.toastr.warning(this.translate.instant('PHARMACY.SELECT_PATIENT'));
      return;
    }

    const command = this.buildCommand();
    this.isLoading = true;
    this.pharmacyService.dispense(command)
      .pipe(
        finalize(() => this.isLoading = false),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (response) => {
          if (response.isSuccess) {
            this.billResult = response.result;
            this.billSaved = true;
            this.toastr.success(this.translate.instant('PHARMACY.BILL_CREATED'));
            this.switchToViewMode();
          } else {
            const allErrors = Object.values(response.errors).flat();
            this.toastr.error(allErrors.join('\n'));
          }
        },
        error: (err) => {
          const msg = err?.error?.errors
            ? Object.values(err.error.errors).flat().join('\n')
            : err?.error?.title || 'Failed to save dispense bill';
          this.toastr.error(msg);
        }
      });
  }

  private switchToViewMode(): void {
    this.isViewMode = true;
    this.activeTabIndex = 0;
    this.updatePaymentSummaryFromBill();
    this.loadPaymentHistory();
    this.cdr.detectChanges();
  }

  private buildCommand(): PharmacyBillCommand {
    const items = this.items.controls.map(row => ({
      drugInventoryId: row.get('drugInventoryId')?.value,
      drugBatchId: row.get('drugBatchId')?.value,
      drugName: row.get('drugName')?.value,
      batchNo: row.get('batchNo')?.value,
      expiryDate: row.get('expiryDate')?.value,
      unitPrice: Number(row.get('unitPrice')?.value),
      gstPercentage: Number(row.get('gstPercentage')?.value),
      quantity: Number(row.get('quantity')?.value),
      totalPrice: Number(row.get('totalPrice')?.value),
    }));

    return {
      patientId: this.selectedPatientId!,
      doctorId: this.selectedDoctorId ?? undefined,
      discountType: this.dispenseForm.get('discountType')?.value,
      discount: Number(this.dispenseForm.get('discount')?.value) || 0,
      amount: this.grandTotal,
      subTotal: this.subTotal,
      totalGST: this.totalGST,
      roundOff: this.roundOff || undefined,
      items,
    };
  }

  // ==========================================
  // AUTOCOMPLETE
  // ==========================================

  private setupPatientAutocomplete(): void {
    this.filteredPatients$ = this.patientCtrl.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(value => {
        const search = typeof value === 'string' ? value : (value as any)?.name || '';
        return this.lookupService.getPatient(search);
      })
    );
  }

  private setupDoctorAutocomplete(): void {
    this.filteredDoctors$ = this.doctorCtrl.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(() => this.lookupService.getDoctors())
    );
  }

  displayPatient(patient: CommonDropDown): string {
    return patient ? patient.name : '';
  }

  displayDoctor(doctor: CommonDropDown): string {
    return doctor ? doctor.name : '';
  }

  onPatientSelected(patient: CommonDropDown): void {
    this.selectedPatientId = patient.id;
  }

  onDoctorSelected(doctor: CommonDropDown): void {
    this.selectedDoctorId = doctor.id;
    this.fetchDoctorInfo(doctor.id);
  }

  // ==========================================
  // SHARED
  // ==========================================

  private loadInventoryLookup(): void {
    this.pharmacyService.getInventoryLookup()
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        if (response.isSuccess) {
          this.inventoryLookup = response.result;
        }
      });
  }

  onClose(): void {
    if (!this.billSaved) {
       this.dialogUtils.closeSidePanel(this.dialogRef);
      return;
    }
     this.dialogUtils.closeSidePanel(this.dialogRef,this.billSaved ? true : undefined);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
